import { z } from 'zod';
import { IMPORT_ENTITIES } from '../enums.js';
export const COMPANY_IMPORT_FIELDS = [
    { key: 'name', labelKey: 'company.name', required: true,
        aliases: ['company', 'company name', 'customer/company', 'customer', 'name', 'organisation', 'organization', 'factory', 'الشركة', 'اسم الشركة', 'المؤسسة'] },
    { key: 'nameAr', labelKey: 'company.nameAr',
        aliases: ['company name arabic', 'arabic name', 'name ar', 'الاسم بالعربية'] },
    { key: 'city', labelKey: 'company.city',
        aliases: ['city', 'town', 'area', 'location', 'region', 'المدينة', 'المنطقة'] },
    { key: 'countryCode', labelKey: 'company.country',
        aliases: ['country', 'country code', 'البلد', 'الدولة'] },
    { key: 'email', labelKey: 'company.email',
        aliases: ['email', 'e-mail', 'email address', 'mail', 'البريد الإلكتروني'] },
    { key: 'phone', labelKey: 'company.phone',
        aliases: ['phone', 'phone number', 'tel', 'telephone', 'mobile', 'contact number', 'الهاتف', 'رقم الهاتف'] },
    { key: 'website', labelKey: 'company.website',
        aliases: ['website', 'web', 'url', 'site', 'الموقع'] },
    { key: 'address', labelKey: 'company.address',
        aliases: ['address', 'street', 'العنوان'] },
    { key: 'financialNumber', labelKey: 'company.financialNumber',
        aliases: ['financial number', 'mof', 'm.o.f', 'vat number', 'tax number', 'الرقم المالي'] },
    { key: 'notes', labelKey: 'company.notes',
        aliases: ['notes', 'note', 'remarks', 'comment', 'comments', 'step 2', 'step2', 'details', 'ملاحظات'] },
    // A contact can be created alongside the company from the same row, which is
    // how MEFOSA's own lists are shaped.
    { key: 'contactFullName', labelKey: 'import.contactName',
        aliases: ['contact', 'contact name', 'person', 'person in charge', 'contact person', 'manager', 'جهة الاتصال', 'الشخص'] },
    { key: 'contactTitle', labelKey: 'import.contactTitle',
        aliases: ['title', 'position', 'job title', 'designation', 'المسمى الوظيفي'] },
    { key: 'contactEmail', labelKey: 'import.contactEmail',
        aliases: ['contact email', 'person email'] },
    { key: 'contactPhone', labelKey: 'import.contactPhone',
        aliases: ['contact phone', 'mobile', 'cell', 'person phone'] },
];
/**
 * MEFOSA's pipeline sheets, from `CRM.xlsx`: Follow ups, Under
 * implementation and Closed.
 *
 * All three share a spine — Sales Person, Date, Company, Contact, Type,
 * Stage, Project Value — and Closed adds the economics: Revenue, Cost,
 * Paid (Y/N), Date Paid. Profit is in their sheet as a column but is NOT
 * imported: it is derived from revenue and cost, so importing it would let a
 * stale figure in.
 */
export const DEAL_IMPORT_FIELDS = [
    { key: 'companyName', labelKey: 'company.name', required: true,
        aliases: ['company', 'company name', 'client', 'customer', 'الشركة', 'اسم الشركة'] },
    { key: 'dealDate', labelKey: 'deal.date', required: true,
        aliases: ['date', 'deal date', 'التاريخ'] },
    { key: 'description', labelKey: 'deal.description',
        aliases: ['description', 'details', 'subject', 'الوصف'] },
    { key: 'quoteNumber', labelKey: 'deal.quoteNumber',
        aliases: ['quota/invoice#', 'quota/invoice #', 'quota/invoice', 'quote #', 'quote number', 'invoice #', 'invoice number', 'ref#', 'ref'] },
    { key: 'salesPerson', labelKey: 'deal.owner',
        aliases: ['sales person', 'sales people', 'salesperson', 'owner', 'مسؤول المبيعات'] },
    { key: 'contactName', labelKey: 'import.contactName',
        aliases: ['contact', 'contact name', 'full name', 'person', 'جهة الاتصال'] },
    { key: 'contactPhone', labelKey: 'import.contactPhone',
        aliases: ['phone', 'phone number', 'tel', 'mobile', 'رقم الهاتف'] },
    { key: 'contactEmail', labelKey: 'import.contactEmail',
        aliases: ['email address', 'email', 'e-mail', 'البريد الإلكتروني'] },
    { key: 'type', labelKey: 'deal.type',
        aliases: ['type', 'project type', 'نوع'] },
    { key: 'stage', labelKey: 'deal.stage',
        aliases: ['stage', 'المرحلة'] },
    { key: 'state', labelKey: 'deal.state',
        aliases: ['state', 'الحالة'] },
    { key: 'projectValue', labelKey: 'deal.projectValue',
        aliases: ['project value', 'value', 'amount', 'قيمة المشروع'] },
    { key: 'revenue', labelKey: 'deal.revenue',
        aliases: ['revenue', 'الإيراد'] },
    { key: 'cost', labelKey: 'deal.cost',
        aliases: ['cost', 'الكلفة'] },
    { key: 'paid', labelKey: 'deal.paid',
        aliases: ['paid (y/n)', 'paid', 'مدفوع'] },
    { key: 'datePaid', labelKey: 'deal.datePaid',
        aliases: ['date paid', 'payment date', 'تاريخ الدفع'] },
    { key: 'notes', labelKey: 'company.notes',
        aliases: ['notes', 'note', 'remarks', 'ملاحظات'] },
];
/**
 * MEFOSA's per-person contact sheets, e.g. `CRM-MS 2026.xlsx`.
 *
 * These are the mirror image of the company lists: the PERSON is the row and
 * the company is one of their attributes. Their real layout is
 * Full Name | Email Address | Phone Number | Company | Role/Position |
 * Contact Type | Notes, under a title block naming whose sheet it is.
 */
export const CONTACT_IMPORT_FIELDS = [
    // Not required: their sheets carry rows with a company and a number but
    // nobody named yet. Those still record a real business, so they import as a
    // company with no contact attached rather than being thrown out.
    { key: 'contactFullName', labelKey: 'import.contactName',
        aliases: ['full name', 'contact name', 'name', 'contact', 'person', 'person in charge', 'contact person', 'الاسم', 'الاسم الكامل', 'جهة الاتصال'] },
    { key: 'contactEmail', labelKey: 'import.contactEmail',
        aliases: ['email address', 'email', 'e-mail', 'mail', 'contact email', 'person email', 'البريد الإلكتروني'] },
    { key: 'contactPhone', labelKey: 'import.contactPhone',
        aliases: ['phone number', 'phone', 'contact numbers', 'contact number', 'tel', 'telephone', 'mobile', 'cell', 'رقم الهاتف', 'الهاتف'] },
    // Required: a person has to belong somewhere. Enforced HERE rather than only
    // at commit time, so the dry run tells the truth — with the rule in one place
    // only, it promised to create a row the commit then refused.
    { key: 'name', labelKey: 'company.name', required: true,
        aliases: ['company', 'company name', 'organisation', 'organization', 'employer', 'factory', 'client', 'الشركة', 'اسم الشركة'] },
    // From the consolidated customer index in CRM.xlsx.
    { key: 'clientType', labelKey: 'company.clientType',
        aliases: ['client type', 'customer type', 'نوع العميل'] },
    { key: 'rating', labelKey: 'company.rating',
        aliases: ['rating', 'stars', 'التقييم'] },
    { key: 'review', labelKey: 'company.review',
        aliases: ['review', 'feedback', 'المراجعة'] },
    { key: 'contactTitle', labelKey: 'import.contactTitle',
        aliases: ['role/position', 'role / position', 'position', 'role', 'title', 'job title', 'designation', 'المسمى الوظيفي'] },
    { key: 'contactType', labelKey: 'import.contactType',
        aliases: ['contact type', 'type', 'category', 'نوع جهة الاتصال'] },
    { key: 'notes', labelKey: 'company.notes',
        aliases: ['notes', 'note', 'remarks', 'comment', 'comments', 'ملاحظات'] },
];
export const IMPORT_FIELDS = {
    company: COMPANY_IMPORT_FIELDS,
    contact: CONTACT_IMPORT_FIELDS,
    deal: DEAL_IMPORT_FIELDS,
};
/** Maps an entity field key to the spreadsheet column header it comes from. */
export const importMappingSchema = z.object({
    mapping: z.record(z.string(), z.string().nullable()),
});
export const importCommitSchema = z.object({
    /** When true, fill blank fields on an existing match instead of skipping it. */
    updateExisting: z.boolean().default(true),
});
export const IMPORT_ENTITY_VALUES = IMPORT_ENTITIES;
/**
 * Auto-detects a column mapping by matching headers against field aliases.
 * Exact alias match wins over a substring match, so "Contact Email" does not
 * get claimed by the plain "email" field.
 */
/**
 * How many of a row's cells look like column headers for this entity.
 *
 * Counting filled cells alone cannot tell a header row from a data row — both
 * are full of text. Matching against the aliases can: a row of people's names
 * scores zero, the real header row scores most of its columns.
 */
export function headerScore(cells, fields) {
    const normalise = (s) => s.toLowerCase().replace(/\s+/g, ' ').trim();
    const seen = new Set();
    let score = 0;
    for (const cell of cells) {
        const n = normalise(cell);
        if (!n || seen.has(n))
            continue;
        seen.add(n);
        if (fields.some((f) => f.aliases.includes(n)))
            score++;
    }
    return score;
}
export function suggestMapping(headers, fields) {
    const mapping = {};
    const normalise = (s) => s.toLowerCase().replace(/\s+/g, ' ').trim();
    const taken = new Set();
    for (const pass of ['exact', 'partial']) {
        for (const field of fields) {
            if (mapping[field.key])
                continue;
            const match = headers.find((h) => {
                if (taken.has(h))
                    return false;
                const n = normalise(h);
                return pass === 'exact'
                    ? field.aliases.includes(n)
                    : field.aliases.some((a) => n.includes(a) || a.includes(n));
            });
            if (match) {
                mapping[field.key] = match;
                taken.add(match);
            }
        }
    }
    for (const field of fields)
        mapping[field.key] ??= null;
    return mapping;
}
//# sourceMappingURL=import.js.map